import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Spacings } from '@styles/index';
import { fetchQuestionMetaData } from '@actions/learnerDoubtsAndSolutions';
import SimpleCanvas from '@educator/Composite/Canvas/SimpleCanvas';
import { useSelector } from 'react-redux';
import { getDoubtsBrowseUrl } from '@api/selectors/urls';
import { getAccessToken, getMeUsername } from '@selectors/auth';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { redirectToRoute } from '@utils/route';
import { useRouter } from 'next/router';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useURLs from '@api/hooks/useURLs';

const moveUp = keyframes`
  to { bottom: 0; }
`;

const moveDown = keyframes`
  to { bottom: -100vh; }
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  background-color: var(--color-base-0);
  position: fixed;
  height: 100vh;
  width: 100vw;
  left: 0;
  z-index: 5;
  ${({ $animateOnMount, $showPlayer }) =>
    $animateOnMount
      ? css`
          bottom: ${$showPlayer ? '-100vh;' : '0;'};
          animation: ${$showPlayer ? moveUp : moveDown} 300ms ease-in;
          animation-fill-mode: forwards;
        `
      : 'bottom: 0;'}
`;

const StyledCanvas = styled(SimpleCanvas)`
  grid-column: 1/25;
`;

const getChapterOrPaperRoute = ({ goal, catalogue, entity }) => ({
  href: '/goal/[goalSlug]/[goalUID]/doubts-and-solutions/[catalogSlug]/[catalogUID]/[catalogEntitySlug]/[catalogEntityUID]',
  as: `/goal/${goal.slug}/${goal.uid}/doubts-and-solutions/${catalogue.slug}/${catalogue.uid}/${entity.slug}/${entity.uid}`
});

const CatalogQuestion = ({
  catalogQuestionUID,
  useAsModal,
  onIframeClose,
  goalUID
}) => {
  const playerUrl = useURLs(getDoubtsBrowseUrl);
  const questionMetadata = useSelector(
    ({ learnerDoubtsAndSolutions }) =>
      learnerDoubtsAndSolutions.questionMetadata[catalogQuestionUID]
  );
  const accessToken = useSelector(getAccessToken);
  const meUsername = useSelector(getMeUsername);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const liveClassData = {
    lessonData: {
      accessToken,
      data: questionMetadata,
      replay: 'true',
      role: 'listener',
      showBackButton: true,
      user_name: meUsername
    },
    isDoubtsSolutions: true,
    isDarkModeOn,
    apiHost: process.env.BACKEND_URL.slice(0, -1)
  };

  const [showPlayer, setShowPlayer] = useState(true);
  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const closePage = () => {
    if (useAsModal) setShowPlayer(false);
    else onIframeClose();
  };

  const router = useRouter();
  const onIframeMessageReceived = (e) => {
    if (e.data.closeIframe) {
      const route = getChapterOrPaperRoute(questionMetadata);
      router.push(route.href, route.as, {
        scroll: !useAsModal,
        shallow: useAsModal
      });
      closePage();
    }
  };

  const onAnimationEnd = (e) => {
    if (!showPlayer && e.target === e.currentTarget) {
      onIframeClose();
    }
  };

  useEffect(() => {
    if (useAsModal) {
      const onPopState = () => {
        const url = new URL(
          `${window.location.origin}${window.history.state.url}`
        );
        if (
          url.pathname !==
          '/doubts-and-solutions/[catalogEntitySlug]/[catalogEntityUID]/[catalogQuestionUID]'
        ) {
          // close iframe using animation
          closePage();
          window.removeEventListener('popstate', onPopState);
        }
      };
      window.addEventListener('popstate', onPopState);
      return () => {
        window.removeEventListener('popstate', onPopState);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getAdditionalEvtData = () => {
    return {
      doubt_type: 'Doubt Browse',
      feature_version: 2,
      flow_type: 'Non_Demo',
      ...routeGoalSegmentData
    };
  };
  return (
    <Wrapper
      $animateOnMount={useAsModal}
      $showPlayer={showPlayer}
      onAnimationEnd={onAnimationEnd}
    >
      <StyledCanvas
        permissions="autoplay; fullscreen;"
        recordingURL={playerUrl}
        liveClassData={liveClassData}
        onMessage={onIframeMessageReceived}
        additionalEvtData={getAdditionalEvtData()}
      />
    </Wrapper>
  );
};

CatalogQuestion.pageInfo = {
  name: 'Doubts & Solutions - Solution',
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true
  }
};

CatalogQuestion.propTypes = {
  catalogQuestionUID: PropTypes.string.isRequired,
  useAsModal: PropTypes.bool,
  onIframeClose: PropTypes.func
};

CatalogQuestion.defaultProps = {
  useAsModal: false,
  onIframeClose: () => {}
};

export default CatalogQuestion;

export const getCatalogQuestionPageProps = async ({
  store,
  catalogQuestionUID,
  isServer,
  res
}) => {
  if (
    !store.getState().learnerDoubtsAndSolutions.questionMetadata[
      catalogQuestionUID
    ]
  )
    await store.dispatch(fetchQuestionMetaData(catalogQuestionUID));
  const state = store.getState();
  if (!state.auth.isLoggedIn) {
    const questionMetadata =
      store.getState().learnerDoubtsAndSolutions.questionMetadata[
        catalogQuestionUID
      ];
    const route = getChapterOrPaperRoute(questionMetadata);
    return redirectToRoute(isServer, res, route.href, route.as);
  }
  return { catalogQuestionUID };
};
